<div class="mx-auto max-w-5xl space-y-4 p-4">

  <!-- ── Seitenkopf ───────────────────────────────────────────────────────────────────────────────────────
       The page says what it governs and for whom, because every action underneath it is irreversible. -->
  <div class="flex items-start gap-3">
    <span class="inline-flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl
                 bg-theme-primary-50 text-theme-primary-600">
      <svg class="h-6 w-6" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
        <path d="M13 19h1a1 1 0 0 1 1 1h7v2h-7a1 1 0 0 1-1 1h-4a1 1 0 0 1-1-1H2v-2h7a1 1 0 0 1 1-1h1v-1.66C8.07 16.13 6 13 6 9.67v-4L12 3l6 2.67v4c0 3.33-2.07 6.46-5 7.67zM12 5L8 6.69V10h4zm0 5v6c1.91-.47 4-2.94 4-5v-1z"/>
      </svg>
    </span>
    <div class="min-w-0">
      <h1 class="text-xl font-bold tracking-tight text-gray-900">Rechtliches</h1>
      <p class="mt-1 text-sm text-gray-500">
        Fiskal-Provider und technische Sicherheitseinrichtung eines Standorts. Jede Aktion auf dieser Seite
        wirkt sofort und lässt sich nicht zurücknehmen.
      </p>
    </div>
  </div>

  <!-- ── Standort wählen ──────────────────────────────────────────────────────────────────────────────────
       The context band. It is the first card because everything below it is scoped to this one pick. -->
  <section aria-labelledby="owner-legal-context-title"
           class="rounded-2xl border-[1px] border-gray-100 bg-white p-4 shadow-sm">
    <h2 class="text-base font-bold tracking-tight text-gray-800" id="owner-legal-context-title">Standort</h2>
    <p class="mt-1 text-xs text-gray-500">
      Wählen Sie das Unternehmen und darin den Standort, dessen rechtliche Einrichtung Sie bearbeiten
      möchten.
    </p>

    @if (loading()) {
      <app-loading/>
    } @else if (loadError(); as message) {
      <div class="mt-3 rounded-xl border-[1px] border-red-100 bg-red-50 p-4 text-sm text-red-700" role="alert">
        <p class="font-semibold">{{ message }}</p>
        <button (click)="load()"
                class="mt-3 inline-flex items-center justify-center rounded-2xl border-[1px] border-red-200
                       bg-white px-4 py-2.5 text-sm font-semibold text-red-700 shadow-sm transition-all
                       duration-200 hover:bg-red-100 active:scale-95"
                type="button">
          Erneut laden
        </button>
      </div>
    } @else {
      <div class="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
        <div>
          <label class="mb-2 block text-sm font-bold tracking-tight text-gray-700" for="owner-legal-company">
            Unternehmen
          </label>
          <cqme-select (currentChange)="chooseCompany(one($event))" [clearable]="true"
                       [current]="companyId() ?? undefined" [labelKey]="'name'" [options]="companies()"
                       [valueKey]="'id'" ariaLabel="Unternehmen, dessen Standort bearbeitet wird"
                       id="owner-legal-company" placeholder="Unternehmen wählen …" size="md"/>
        </div>
        <div>
          <label class="mb-2 block text-sm font-bold tracking-tight text-gray-700" for="owner-legal-shard">
            Standort
          </label>
          <cqme-select (currentChange)="chooseShard(one($event))" [clearable]="true"
                       [current]="shardId() ?? undefined" [disabled]="!companyId()" [labelKey]="'name'"
                       [options]="shards()" [valueKey]="'id'"
                       ariaLabel="Standort, dessen rechtliche Einrichtung bearbeitet wird"
                       id="owner-legal-shard" placeholder="Standort wählen …" size="md"/>
        </div>
      </div>

      @if (companyHasNoShards()) {
        <p class="mt-2 text-xs text-gray-500" data-testid="owner-legal-no-shards">
          Dieses Unternehmen hat noch keine Standorte.
        </p>
      }

      @if (shard(); as chosen) {
        <p class="mt-3 flex flex-wrap items-center gap-x-2 gap-y-1 text-sm text-gray-700">
          <span class="font-semibold text-gray-900">{{ chosen.name }}</span>
          <span class="font-mono text-xs text-gray-500">{{ chosen.id }}</span>
        </p>
      }

      <p class="mt-2 text-xs text-gray-500">Die Auswahl wird in diesem Browser gespeichert.</p>
    }
  </section>

  @if (!loading() && !loadError()) {
    @if (shardId(); as shardId) {
      <!-- ── Fiskal-Provider ──────────────────────────────────────────────────────────────────────────────
           Both sections carry their own heading + one sentence, so the page reads top to bottom as one
           screen: which provider signs, and what the device that signs is currently doing. -->
      <section aria-labelledby="owner-legal-provider-title"
               class="rounded-2xl border-[1px] border-gray-100 bg-white p-4 shadow-sm">
        <h2 class="text-base font-bold tracking-tight text-gray-800" id="owner-legal-provider-title">
          Fiskal-Provider
        </h2>
        <p class="mt-1 text-xs text-gray-500">
          Welcher Anbieter für welchen gesetzlichen Bereich dieses Standorts zuständig ist und mit welchen
          Zugangsdaten. Ein Wechsel ist nicht umkehrbar.
        </p>
        <div class="mt-4">
          <app-settings [shardId]="shardId"/>
        </div>
      </section>

      <!-- ── Technische Sicherheitseinrichtung ───────────────────────────────────────────────────────── -->
      <section aria-labelledby="owner-legal-tse-title"
               class="rounded-2xl border-[1px] border-gray-100 bg-white p-4 shadow-sm">
        <h2 class="text-base font-bold tracking-tight text-gray-800" id="owner-legal-tse-title">
          Technische Sicherheitseinrichtung
        </h2>
        <p class="mt-1 text-xs text-gray-500">
          Zustand, Kennzahlen und Lebenszyklus der TSE dieses Standorts.
        </p>
        <div class="mt-4">
          <app-technical-security-system [shardId]="shardId"/>
        </div>
      </section>
    } @else {
      <!-- The designed empty state: no shard is picked, and nothing is guessed for the reader. -->
      <p class="rounded-2xl border-2 border-dashed border-gray-100 py-10 text-center text-sm italic
                text-gray-500"
         data-testid="owner-legal-empty">
        Wählen Sie oben einen Standort, um seine Fiskal-Provider und seine technische
        Sicherheitseinrichtung zu sehen.
      </p>
    }
  }
</div>
